import React, { ReactNode } from 'react';
import moment from 'moment';
import { z } from 'zod';
import { Box, Tooltip } from '@mui/material';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  FormSwitch,
  InputField,
  FormRadioGroup,
  FormInputSelect,
  NumberFormatField,
  InterestInputField,
  MultiSelectWithCustomOptions,
} from 'components/form';
import { FeeArrayFields } from './FeeArrayFields';
import { FinanceStructureSchema } from '../model';
import { Structures, RateTypes, ProgramTypes } from '../types';
import { StructureConfiguration } from './StructureConfiguration';

import { calcTermsSelectOptions } from '../../../lib';
import { paymentCalculationMethodOptions, programTypeOptions, rateTypeOptions } from '../constants';

export const FinanceCommonFields = ({ netInvoiceAmount }: { netInvoiceAmount: number }) => {
  const { control, setValue } = useFormContext<z.infer<typeof FinanceStructureSchema>>();
  const { fields: specifyRatesFields } = useFieldArray({ control, name: 'financeQuote.specifyRates' });
  const { fields: specifyLenderRatesFields } = useFieldArray({
    control,
    name: 'financeQuote.subsidyCapConfiguration.lenderMarketRates',
  });

  const financeQuote = useWatch({ control, name: 'financeQuote' });

  const { terms, specifyRate, commonRate, programType, rateType, startDate, daysToFirstPayment } = financeQuote;

  const handleChangeTerms = (selected: (string | number)[]) => {
    if (selected.length === terms.length) return;

    const termsToString = selected.toSorted((a, b) => Number(a) - Number(b)).map(String);

    setValue('financeQuote.terms', termsToString);

    if (specifyRate) {
      const nextSpecifyRates = termsToString.map(term => ({ term, rate: 0 }));
      setValue('financeQuote.specifyRates', nextSpecifyRates);

      const nextSpecifyLenderMarketRates = termsToString.map(term => ({ term: Number(term), rate: 0 }));
      setValue('financeQuote.subsidyCapConfiguration.lenderMarketRates', nextSpecifyLenderMarketRates);
    } else {
      setValue(
        'financeQuote.specifyRates',
        termsToString.map(term => ({ term, rate: commonRate }))
      );
      setValue(
        'financeQuote.subsidyCapConfiguration.lenderMarketRates',
        termsToString.map(term => ({ term: Number(term), rate: 0 }))
      );
    }
  };

  const handleStartDateChange = (value: string) => {
    try {
      const firstPaymentDateMoment = moment(value);
      setValue(
        'financeQuote.firstPaymentDate',
        firstPaymentDateMoment.add(daysToFirstPayment, 'days').format('YYYY-MM-DD')
      );
    } catch (e: any) {
      console.error(`Error in handleStartDateChange: ${e.message}`);
    }
  };

  const handleFirstPaymentDateChange = (value: string) => {
    const firstPaymentDateMoment = moment(value);
    const startDateMoment = moment(startDate);

    if (firstPaymentDateMoment.isBefore(startDateMoment)) {
      setValue('financeQuote.startDate', firstPaymentDateMoment.format('YYYY-MM-DD'));
      setValue('financeQuote.daysToFirstPayment', '0');
    } else {
      setValue('financeQuote.daysToFirstPayment', firstPaymentDateMoment.diff(startDateMoment, 'days').toString());
    }
  };

  const handleDaysToFirstPaymentChange = (value: string) => {
    const firstPaymentDateMoment = moment(startDate);
    firstPaymentDateMoment.add(value, 'days');
    setValue('financeQuote.firstPaymentDate', firstPaymentDateMoment.format('YYYY-MM-DD'));
  };

  return (
    <>
      <Box className="row">
        <Controller
          control={control}
          name="financeQuote.rateType"
          render={({ field }) => (
            <FormRadioGroup
              row
              label="Rate type"
              className="flexed-radio"
              options={rateTypeOptions}
              {...field}
              onChange={(event, value) => {
                if (value === RateTypes.RateFactor) setValue('financeQuote.specifyRate', true);
                setValue(
                  'financeQuote.specifyRates',
                  terms.toSorted().map(term => ({ term, rate: 0 }))
                );
                field.onChange(event, value);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="financeQuote.specifyRate"
          render={({ field }) => (
            <FormSwitch
              label="Specify rate per term"
              disabled={rateType === RateTypes.RateFactor}
              {...field}
              checked={field.value}
            />
          )}
        />
      </Box>

      <Box className="row">
        <MultiSelectWithCustomOptions
          isOptionsSorted
          control={control}
          name="financeQuote.terms"
          onChange={handleChangeTerms}
          options={calcTermsSelectOptions(terms)}
          selectProps={{ label: 'Terms in Months' }}
          onCustomOptionAdded={() => {
            setValue('financeQuote.customTermTemporaryValue', null);
          }}
          selectFormControlProps={{ required: true, variant: 'standard' }}
          inputProps={{
            type: 'number',
            label: 'Add custom term',
            placeholder: 'Enter your own term',
            onInputChanged: value => {
              setValue('financeQuote.customTermTemporaryValue', value);
            },
          }}
        />

        {!specifyRate && (
          <InterestInputField
            control={control}
            label={financeQuote.structure === Structures.Subsidy ? 'Subsidized Rate (%)' : 'Buy Rate (%)'}
            name="financeQuote.commonRate"
            textFieldProps={{ required: true, fullWidth: true, size: 'small' }}
          />
        )}
      </Box>

      {!!specifyRate && !!specifyRatesFields?.length && (
        <>
          {specifyRatesFields
            .toSorted((a, b) => +a.term - +b.term)
            .map((field, index) => {
              const rateFields: Record<RateTypes, ReactNode> = {
                [RateTypes.InterestRate]: (
                  <Box className="row">
                    <InterestInputField
                      key={field.id}
                      control={control}
                      name={`financeQuote.specifyRates.${index}.rate`}
                      textFieldProps={{ required: true, fullWidth: true }}
                      label={`${financeQuote.structure === Structures.Subsidy ? 'Subsidized' : 'Buy'} Rate ${field?.term ?? ''} months (%)`}
                    />
                    {financeQuote.configureSubsidyCap && (
                      <InterestInputField
                        key={specifyLenderRatesFields[index].id}
                        control={control}
                        name={`financeQuote.specifyLenderMarketRates.${index}.rate`}
                        textFieldProps={{ required: true, fullWidth: true }}
                        label={`Lender Market Rate ${field?.term ?? ''} months (%)`}
                      />
                    )}
                  </Box>
                ),
                [RateTypes.RateFactor]: (
                  <NumberFormatField
                    key={field.id}
                    range={[0, 1]}
                    decimalScale={6}
                    control={control}
                    fixedDecimalScale
                    thousandSeparator
                    name={`financeQuote.specifyRates.${index}.rate`}
                    textFieldProps={{ required: true, fullWidth: true }}
                    label={`Buy Rate ${field?.term ?? ''} months (Rate Factor)`}
                  />
                ),
              };

              return <>{rateFields[rateType]}</>;
            })}
        </>
      )}

      <StructureConfiguration netInvoiceAmount={netInvoiceAmount} />

      <Box className="row">
        <Controller
          control={control}
          name="financeQuote.programType"
          render={({ field }) => (
            <FormInputSelect variant="standard" label="Program Type" options={programTypeOptions} {...field} />
          )}
        />

        <InterestInputField
          control={control}
          label="Minimum down payment(%)"
          name="financeQuote.minimumRequiredDownPayment"
          textFieldProps={{ required: false, fullWidth: true, size: 'small' }}
        />

        {programType === ProgramTypes.Lease && (
          <InterestInputField
            control={control}
            label="Residual (%)"
            name="financeQuote.residual"
            textFieldProps={{ fullWidth: true }}
          />
        )}
      </Box>

      <Box className="row">
        <InputField
          control={control}
          label="Advance Payment (# of)"
          name="financeQuote.advancePayment"
          inputProps={{ type: 'number' }}
        />

        <InputField
          control={control}
          label="Contract Date"
          name="financeQuote.startDate"
          inputProps={{ type: 'date', onKeyDown: e => e.preventDefault() }}
          extraAction={{
            debounceTime: 50,
            cb: nextValue => {
              handleStartDateChange(nextValue);
            },
          }}
        />

        <InputField
          control={control}
          label="First Payment Date"
          name="financeQuote.firstPaymentDate"
          inputProps={{ type: 'date', onKeyDown: e => e.preventDefault() }}
          extraAction={{
            debounceTime: 50,
            cb: nextValue => {
              handleFirstPaymentDateChange(nextValue);
            },
          }}
        />

        <InputField
          control={control}
          label="Days to First Payment"
          name="financeQuote.daysToFirstPayment"
          inputProps={{ type: 'number' }}
          extraAction={{
            debounceTime: 50,
            cb: nextValue => {
              if (+nextValue >= 0) {
                handleDaysToFirstPaymentChange(nextValue);
              } else {
                setValue('financeQuote.daysToFirstPayment', '0');
              }
            },
          }}
        />
      </Box>

      <Controller
        control={control}
        name="financeQuote.paymentCalculationMethod"
        render={({ field }) => (
          <Tooltip
            arrow
            title={
              'Advance - Payment is due at the beginning of the period\nArrears - Payment is due at the end of the period'
            }
          >
            <span>
              <FormInputSelect
                variant="standard"
                label="Payment Calculation Method"
                options={paymentCalculationMethodOptions}
                {...field}
              />
            </span>
          </Tooltip>
        )}
      />

      <FeeArrayFields />
    </>
  );
};
